import React, {
  FC,
  useEffect,
  useState,
  useRef,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { ModalProps } from './Modal.types';
import { Button } from '../Button';
import { ResponsivePadding } from '../ResponsivePadding';
import { usePrevious } from '../../hooks';

const Content = styled.div`
  position: relative;
  margin: 4rem auto;
  border-radius: 6px;
  max-width: calc(100vw - 2rem);
  min-height: 10rem;
  z-index: 1000;
  box-sizing: border-box;
  background-color: white;
  box-shadow: rgba(0, 0, 0, 0.176) 0px 4px 8px;
  width: ${(props: Partial<ModalProps>) => props.width};

  @media (max-width: 1000px) {
    margin: 1rem auto;
  }

  &.-entering {
    animation: fade-in 0.15s ease-in both, grow 0.1s ease-in both;
  }

  &.-exiting {
    animation: fade-out 0.15s ease-in both, shrink 0.1s ease-in both;
  }
`;

const Header = styled.div`
  padding: 1rem;
  height: 48px;
  box-sizing: border-box;

  border-bottom: 1px solid rgb(211, 211, 211);
`;

const CloseButton = styled(Button)`
  position: absolute;
  top: 0;
  right: 0;
  height: 48px;
  width: 48px;
  font-size: 1.5rem;
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 999;
  overflow-y: auto;
  background-color: rgba(0, 0, 0, 0.5);

  &.-entering {
    animation: fade-in 0.1s ease-in both;
  }

  &.-exiting {
    animation: fade-out 0.15s ease-in both;
  }
`;

export const Modal: FC<ModalProps> = props => {
  const { title, width, isClosable, onClose, isOpen: _, ...rest } = props;
  const className = props.isOpen ? '-entering' : '-exiting';

  const [isOpen, setIsOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const wasOpen = usePrevious(props.isOpen || false);

  const onKeyUp = useCallback(
    (e: KeyboardEvent) => {
      if (e.keyCode === 27 && isClosable) {
        // Esc key was pressed
        onClose();
      }
    },
    [onClose, isClosable],
  );

  const onIsOpenChanged = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        // prevent scrolling of main body when modal is open
        document.body.style.overflow = 'hidden';
        window.addEventListener('keyup', onKeyUp);
        setIsOpen(true);
      } else {
        // restore scrolling behavior
        document.body.style.overflow = 'unset';
        window.removeEventListener('keyup', onKeyUp);
        setTimeout(() => {
          // wait for close animation to finish before actually closing
          setIsOpen(false);
        }, 150);
      }
    },
    [setIsOpen, onKeyUp],
  );

  const onOutsideClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      const shouldClose =
        isClosable &&
        modalRef.current &&
        !modalRef.current.contains(e.target as Node);

      if (shouldClose) onClose();
    },
    [modalRef, isClosable, onClose],
  );

  useEffect(() => {
    // using ! as to not compare `false` to `undefined`
    if (!props.isOpen !== !wasOpen) {
      return onIsOpenChanged(props.isOpen);
    }
  }, [props.isOpen, wasOpen, onIsOpenChanged]);

  return (isOpen
    ? createPortal(
      <Overlay
        aria-modal="true"
        tabIndex={-1}
        className={className}
        onClick={onOutsideClick}
      >
        <Content width={width} ref={modalRef} className={className} {...rest}>
          {title && (
            <Header>
              {typeof title === 'string' ? <strong>{title}</strong> : title}
            </Header>
          )}
          {isClosable && (
            <CloseButton
              variant="plain"
              size="small"
              data-testid="modal-close-button"
              onClick={onClose}
            >
              ×
            </CloseButton>
          )}
          {typeof props.children === 'string' ? (
            <ResponsivePadding desktop="1rem" mobile="1rem">
              {props.children}
            </ResponsivePadding>
          ) : (
            props.children
          )}
        </Content>
      </Overlay>,
      document.body,
    )
    : null
  );
};

Modal.defaultProps = {
  width: '36rem',
  isClosable: true,
};
